import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@bloomcode/ui';
import { Download, ChevronLeft, ChevronRight, FileText } from '@bloomcode/icons';
import { useInvoices } from '@/hooks';
import type { PaymentStatus } from '@bloomcode/graphql';

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR' }).format(
    amount / 100
  );
}

function statusBadgeVariant(status: string): 'default' | 'success' | 'destructive' | 'warning' {
  switch (status) {
    case 'PAID':
      return 'success';
    case 'OVERDUE':
      return 'destructive';
    case 'OPEN':
      return 'warning';
    default:
      return 'default';
  }
}

const STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'All Statuses', value: 'ALL' },
  { label: 'Open', value: 'OPEN' },
  { label: 'Paid', value: 'PAID' },
  { label: 'Overdue', value: 'OVERDUE' },
  { label: 'Void', value: 'VOID' },
];

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  const { invoices, pagination, loading, error } = useInvoices({
    page,
    limit: 20,
    status: statusFilter !== 'ALL' ? (statusFilter as PaymentStatus) : undefined,
  });

  const handleDownload = (invoiceUrl: string | null | undefined, e: React.MouseEvent) => {
    e.stopPropagation();
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <Helmet>
        <title>Invoices | Bloomcode Ledger Portal</title>
        <meta name="description" content="View and download all your invoices." />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-[Manrope]">Invoices</h1>
            <p className="text-muted-foreground mt-1">View and download your invoices</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {pagination ? `${pagination.total} invoice${pagination.total !== 1 ? 's' : ''}` : 'Invoices'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {error ? (
              <div className="p-8 text-center text-destructive">
                Failed to load invoices. Please try again.
              </div>
            ) : loading ? (
              <div className="divide-y">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between px-6 py-4">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-8 w-24" />
                  </div>
                ))}
              </div>
            ) : invoices.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No invoices found.</p>
              </div>
            ) : (
              <>
                {/* Table Header */}
                <div className="hidden md:grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-4 px-6 py-3 bg-muted/50 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  <span>Invoice #</span>
                  <span>Date</span>
                  <span>Amount</span>
                  <span>Status</span>
                  <span>Actions</span>
                </div>
                <div className="divide-y">
                  {invoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 md:gap-4 px-6 py-4 cursor-pointer hover:bg-muted/30 transition-colors items-center"
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                    >
                      <span className="font-medium text-sm">{invoice.invoice_number}</span>
                      <span className="text-sm text-muted-foreground">
                        {invoice.created_at
                          ? new Date(invoice.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })
                          : '—'}
                      </span>
                      <span className="text-sm font-semibold">
                        {formatCurrency(invoice.total, invoice.currency)}
                      </span>
                      <Badge variant={statusBadgeVariant(invoice.status)} className="w-fit">
                        {invoice.status}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1"
                        disabled={!invoice.invoice_url}
                        onClick={(e) => handleDownload(invoice.invoice_url, e)}
                      >
                        <Download className="h-4 w-4" />
                        <span className="hidden sm:inline">PDF</span>
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.pages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination.hasPrev}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination.hasNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
